import {
  CreditNotePayoutType,
  InvoiceGetResponseType,
  InvoicePayoutType,
} from "@sellar-io/api-v2/types";
import dayjs from "dayjs";
import { formatCurrency } from "@sellar-io/common";
import { SELLAR_BUSINESS_ID } from "../../../../constants/sellarConstants.shared";

const SellarCreditInvoice = ({ invoice }: { invoice?: InvoicePayoutType }) => {
  if (!invoice) return;

  const vatString =
    invoice.SenderBusinessId === SELLAR_BUSINESS_ID
      ? "VAT - Exempt"
      : "VAT - GB (20%)";
  return (
    <div className="p-4">
      <div className="grid grid-cols-2 gap-4 p-3 text-sm font-normal text-zinc-800">
        <div className="font-semibold">To</div>
        <div>{invoice.RecipientBusiness.name || ""}</div>
        <div className="font-semibold">From</div>
        <div>{invoice.SenderBusiness.name || ""}</div>
        <div className="font-semibold">Invoice</div>
        <div>{invoice.customId || ""}</div>
        <div className="font-semibold">Memo</div>
        {invoice.SenderBusinessId === SELLAR_BUSINESS_ID ? (
          <div>Sellar Credit fees</div>
        ) : (
          <div>
            {invoice.Order?.Retailer?.Business?.name || ""} - Order #
            {invoice.OrderId || ""}
          </div>
        )}
      </div>
      <div className="mt-4 rounded-lg border border-zinc-300 pt-4">
        <div className="mb-4 w-full px-4">
          <span className="block w-full border-b border-zinc-300 pb-2 text-sm font-semibold text-zinc-500">
            Items • x{invoice.InvoiceLineItems.length}
          </span>
        </div>
        <div className="mt-2 flex flex-col gap-4 border-b border-zinc-300 px-4 pb-4">
          {invoice.InvoiceLineItems.map((lineItem) => (
            <div key={lineItem.id} className="flex flex-col gap-2">
              <span>{dayjs(lineItem.createdAt).format("DD/MM/YYYY")}</span>
              <div className="flex items-center justify-between gap-2 text-sm font-semibold">
                <span>{lineItem.description}</span>
                <span>{formatCurrency(lineItem.subtotal)}</span>
              </div>
              <span className="text-sm text-zinc-500">
                Qty {lineItem.quantity}
              </span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-4 p-4">
          <div className="flex items-center justify-between gap-2 text-sm font-semibold">
            <span>Subtotal</span>
            <span>{formatCurrency(invoice.subtotal)}</span>
          </div>

          <div className="flex items-center justify-between gap-2">
            <span>{vatString}</span>
            <span>{formatCurrency(invoice.total - invoice.subtotal)}</span>
          </div>

          <div className="flex items-center justify-between gap-2 text-sm font-semibold">
            <span>Total due</span>
            <span>{formatCurrency(invoice.total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Invoice = ({ invoice }: { invoice?: InvoiceGetResponseType }) => {
  if (!invoice) return;

  return (
    <div className="p-4">
      <div className="grid grid-cols-2 gap-4 p-3 text-sm font-normal text-zinc-800">
        <div className="font-semibold">To</div>
        <div>{invoice.RecipientBusiness.name || ""}</div>
        <div className="font-semibold">From</div>
        <div>Sellar</div>
        <div className="font-semibold">Invoice</div>
        <div>No. {invoice.customId || invoice.stripeInvoiceId || ""}</div>
        {/* NOTE: this will become dynamic with other invoice types */}
        <div className="font-semibold">Memo</div>
        <div>Sellar Market - Opening order fee</div>
      </div>
      <div className="mt-4 rounded-lg border border-zinc-300 pt-4">
        <div className="mb-4 w-full px-4">
          <span className="block w-full border-b border-zinc-300 pb-2 text-sm font-semibold text-zinc-500">
            Items • x{invoice.InvoiceLineItems.length}
          </span>
        </div>
        <div className="mt-2 flex flex-col gap-4 border-b border-zinc-300 px-4 pb-4">
          {invoice.InvoiceLineItems.map((lineItem, idx) => (
            <div key={idx} className="flex flex-col gap-2">
              <span>{dayjs(lineItem.createdAt).format("DD/MM/YYYY")}</span>
              <div className="flex items-center justify-between gap-2 text-sm font-semibold">
                <span>{lineItem.description}</span>
                <span>{formatCurrency(lineItem.subtotal)}</span>
              </div>
              <span className="text-sm text-zinc-500">
                Qty {lineItem.quantity}
              </span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-4 p-4">
          <div className="flex items-center justify-between gap-2 text-sm font-semibold">
            <span>Subtotal</span>
            <span>{formatCurrency(invoice.subtotal)}</span>
          </div>

          <div className="flex items-center justify-between gap-2">
            <span>VAT - GB (20%)</span>
            <span>{formatCurrency(invoice.total - invoice.subtotal)}</span>
          </div>

          <div className="flex items-center justify-between gap-2 text-sm font-semibold">
            <span>Total due</span>
            <span>{formatCurrency(invoice.total)}</span>
          </div>
        </div>
      </div>
      {/* TODO: email invoice */}
      {/* <div className="mt-4 flex flex-col gap-3">
        <H5>Email invoice:</H5>
        <InputContainer>
          <InputEmail placeholder="" />
        </InputContainer>
        <Button color="secondary">Send invoice</Button>
      </div> */}
    </div>
  );
};

export const CreditNote = ({
  creditNote,
}: {
  creditNote?: CreditNotePayoutType;
}) => {
  if (!creditNote) return null;

  return (
    <div className="p-4">
      <div className="grid grid-cols-2 gap-4 p-3 text-sm font-normal text-zinc-800">
        <div className="font-semibold">To</div>
        <div>{creditNote.SenderBusiness.name || ""}</div>
        <div className="font-semibold">From</div>
        <div>{creditNote.RecipientBusiness.name || ""}</div>

        <div className="font-semibold">Credit Note</div>
        <div>No. {creditNote.customId || creditNote.id}</div>

        {/* Use the memo field or something similar if applicable */}
        <div className="font-semibold">Memo</div>
        <div>{creditNote.memo || ""}</div>
      </div>

      <div className="mt-4 rounded-lg border border-zinc-300 pt-4">
        <div className="mb-4 w-full px-4">
          <span className="block w-full border-b border-zinc-300 pb-2 text-sm font-semibold text-zinc-500">
            Items • x{creditNote.CreditNoteLineItems.length}
          </span>
        </div>
        <div className="mt-2 flex flex-col gap-4 border-b border-zinc-300 px-4 pb-4">
          {creditNote.CreditNoteLineItems.map((lineItem, idx) => (
            <div key={idx} className="flex flex-col gap-2">
              <span>{dayjs(lineItem.createdAt).format("DD/MM/YYYY")}</span>
              <div className="flex items-center justify-between gap-2 text-sm font-semibold">
                <span>{lineItem.description}</span>
                <span>{formatCurrency(lineItem.subtotal)}</span>
              </div>
              <span className="text-sm text-zinc-500">
                Qty {lineItem.quantity}
              </span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-4 p-4">
          <div className="flex items-center justify-between gap-2 text-sm font-semibold">
            <span>Subtotal</span>
            <span>{formatCurrency(creditNote.subtotal)}</span>
          </div>

          <div className="flex items-center justify-between gap-2">
            <span>VAT - GB (20%)</span>
            <span>
              {formatCurrency(creditNote.total - creditNote.subtotal)}
            </span>
          </div>

          <div className="flex items-center justify-between gap-2 text-sm font-semibold">
            <span>Total due</span>
            {/* Some teams prefer to show negative sign for credit notes */}
            <span>-{formatCurrency(creditNote.total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export {
  Invoice as FinanceDrawerInvoice,
  CreditNote as FinanceDrawerCreditNote,
  SellarCreditInvoice as FinanceDrawerSellarCreditInvoice,
};
